import { useQuery } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { backendService } from "../../services/backend.service";
import type { NurseFullRole } from "../../utils/models";

export interface ShiftHourOption {
	start_time: string;
	end_time: string;
	ratio: number;
}

/** A role/job selection as the user picked it in the header. */
export type RoleJobSelection = {
	roles: NurseFullRole[];
	jobs: string[];
};

/** The role/job filter whose Shift Hours options have loaded; reports run against this. */
type AppliedScope = {
	departmentId: string;
	roleFilter?: NurseFullRole[];
	jobFilter?: string[];
	options: ShiftHourOption[];
	/** Restored if the next selection's options fail to load. */
	selection: RoleJobSelection;
};

const FILTER_DEBOUNCE_MS = 750;
const OPTIONS_STALE_TIME_MS = 5 * 60 * 1000;
const SHIFT_HOURS_PARAM = "shiftHours";

/** Format a time string like "07:00:00" to "7:00" for display. */
export const formatShiftTime = (t: string): string => {
	const [h, m] = t.split(":");
	return `${Number.parseInt(h, 10)}:${m}`;
};

/** Build the "HH:MM-HH:MM" key used in URL params and API calls. */
export const toShiftHourKey = (opt: ShiftHourOption): string => {
	const fmtTime = (t: string) => t.slice(0, 5); // "07:00:00" → "07:00"
	return `${fmtTime(opt.start_time)}-${fmtTime(opt.end_time)}`;
};

const useCommonShiftHours = (departmentId: string, roleFilter?: NurseFullRole[], jobFilter?: string[]) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isAnyDemo } = useDepartmentFlags();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);

	return useQuery({
		queryKey: ["common-shift-hours", departmentId, roleFilter, jobFilter, jobManagementEnabled, chosenDepartment.jobIds],
		queryFn: async () => {
			const result = await backendService.getCommonShiftHours(departmentId, roleFilter, jobFilter);
			return result.data;
		},
		staleTime: OPTIONS_STALE_TIME_MS,
		enabled: !isAnyDemo,
	});
};

/**
 * Shift Hours options and selection for the current role/job filter.
 *
 * Reports read `appliedRoleFilter`/`appliedJobFilter`, not the header selection: those only move once
 * the new filter's options have loaded and the selected times are reconciled, so a report never runs
 * the new role against the previous role's time window.
 */
export const useShiftHourScope = ({
	departmentId,
	trackPrefix,
	roleFilter,
	jobFilter,
	selection,
	restoreSelection,
}: {
	departmentId: string;
	trackPrefix: string;
	/** Role filter sent to the API; undefined means all roles. */
	roleFilter?: NurseFullRole[];
	/** Job filter sent to the API; undefined means all jobs. */
	jobFilter?: string[];
	selection: RoleJobSelection;
	restoreSelection: (selection: RoleJobSelection) => void;
}) => {
	const { isAnyDemo } = useDepartmentFlags();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const [searchParams, setSearchParams] = useSearchParams();

	const requestedRoleFilter = useDebouncedValue(roleFilter, FILTER_DEBOUNCE_MS);
	const requestedJobFilter = useDebouncedValue(jobFilter, FILTER_DEBOUNCE_MS);
	const debounceSettled = isEqual(roleFilter, requestedRoleFilter) && isEqual(jobFilter, requestedJobFilter);
	const { data: requestedOptions, error: requestError } = useCommonShiftHours(departmentId, requestedRoleFilter, requestedJobFilter);
	const requestFailed = !!requestError && !requestedOptions;
	const settledFailure = requestFailed && debounceSettled;

	const [appliedScope, setAppliedScope] = useState<AppliedScope>();
	const applied = appliedScope?.departmentId === departmentId ? appliedScope : undefined;
	const isApplied = (roles?: NurseFullRole[], jobs?: string[]) => !!applied && isEqual(roles, applied.roleFilter) && isEqual(jobs, applied.jobFilter);
	const unavailable = settledFailure && !applied;
	const updating = !isAnyDemo && !settledFailure && !(isApplied(roleFilter, jobFilter) && isApplied(requestedRoleFilter, requestedJobFilter));
	const ready = isAnyDemo || unavailable || (!!applied && !updating && !requestFailed);

	const optionKeys = useMemo(() => (applied?.options ?? []).map(toShiftHourKey), [applied?.options]);
	const [selectedShiftHours, setSelectedShiftHours] = useState<string[]>(() => searchParams.get(SHIFT_HOURS_PARAM)?.split(",").filter(Boolean) ?? []);
	const validShiftHours = useMemo(() => selectedShiftHours.filter((key) => optionKeys.includes(key)), [optionKeys, selectedShiftHours]);

	// Empty means "All": neither state nor the URL carries every key.
	const writeShiftHours = useCallback(
		(shiftHours: string[]) => {
			setSelectedShiftHours(shiftHours);
			setSearchParams(
				(prev) => {
					const params = new URLSearchParams(prev);
					if (shiftHours.length === 0) params.delete(SHIFT_HOURS_PARAM);
					else params.set(SHIFT_HOURS_PARAM, shiftHours.join(","));
					return params;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	useEffect(() => {
		if (!debounceSettled || !requestedOptions) return;
		const nextKeys = requestedOptions.map(toShiftHourKey);
		const kept = selectedShiftHours.filter((key) => nextKeys.includes(key));
		const removed = selectedShiftHours.filter((key) => !nextKeys.includes(key));
		const next = kept.length === nextKeys.length ? [] : kept;
		setAppliedScope({ departmentId, roleFilter: requestedRoleFilter, jobFilter: requestedJobFilter, options: requestedOptions, selection });
		if (isEqual(next, selectedShiftHours)) return;
		writeShiftHours(next);
		if (removed.length > 0) {
			const key = next.length > 0 ? "managementCenter.shiftHoursSomeRemoved" : "managementCenter.shiftHoursReset";
			showSnackbar(t(key, { times: removed.join(", ") }), "info");
		}
	}, [
		debounceSettled,
		departmentId,
		requestedJobFilter,
		requestedOptions,
		requestedRoleFilter,
		selectedShiftHours,
		selection,
		showSnackbar,
		t,
		writeShiftHours,
	]);

	useEffect(() => {
		if (!debounceSettled || !requestError) return;
		trackError(`${trackPrefix}_shift_hours_options_error`, { error_message: requestError.message, had_options: !!requestedOptions });
		if (unavailable) return;
		showSnackbar(t(requestFailed ? "managementCenter.shiftHoursLoadError" : "managementCenter.shiftHoursRefreshError"), "error");
		if (requestFailed && applied) restoreSelection(applied.selection);
	}, [applied, debounceSettled, requestError, requestFailed, requestedOptions, restoreSelection, showSnackbar, t, trackError, trackPrefix, unavailable]);

	const onShiftHoursChange = useCallback(
		(shiftHours: string[]) => {
			const next = shiftHours.length === optionKeys.length ? [] : shiftHours;
			writeShiftHours(next);
			track(`${trackPrefix}_shift_hours_changed`, { shift_hours: next.join(",") });
		},
		[optionKeys.length, track, trackPrefix, writeShiftHours],
	);

	return {
		// With no options ever loaded, reports fall back to the requested filter and no time filter.
		appliedRoleFilter: unavailable ? requestedRoleFilter : applied?.roleFilter,
		appliedJobFilter: unavailable ? requestedJobFilter : applied?.jobFilter,
		shiftHourOptions: applied?.options,
		selectedShiftHours: validShiftHours.length === 0 ? optionKeys : validShiftHours,
		onShiftHoursChange,
		shiftHoursForApi: validShiftHours.length > 0 && validShiftHours.length < optionKeys.length ? validShiftHours : undefined,
		shiftHoursUpdating: updating,
		shiftHoursUnavailable: unavailable,
		shiftHoursReady: ready,
	};
};
